import React, { useEffect, useState } from 'react';
import { tariffsApi, channelsApi, settingsApi } from '../lib/api';
import { tariffDeepLink } from '../lib/deepLinks';
import { Plus, Pencil, Trash2, Tag, Clock, Star, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

function TariffDeepLink({ link, copied, onCopy, testId }) {
  if (!link) return null;
  return (
    <div className="flex items-center gap-2 mb-4 min-w-0">
      <code className="text-[11px] text-zinc-500 truncate flex-1 min-w-0 font-mono" title={link}>
        {link}
      </code>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs shrink-0"
        onClick={onCopy}
        data-testid={testId}
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
        Скопировать
      </Button>
    </div>
  );
}

const initialFormState = {
  channel_id: '',
  name: '',
  price: '',
  price_stars: '',
  duration_days: '',
  description: '',
  renewal_discount_enabled: false,
  renewal_discount_type: 'percent',
  renewal_discount_value: '',
  renewal_window_before_days: '',
  renewal_window_after_days: '',
};

export default function Tariffs() {
  const [tariffs, setTariffs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [filterChannel, setFilterChannel] = useState('all');
  const [botUsername, setBotUsername] = useState('');
  const [copiedId, setCopiedId] = useState('');

  const fetchData = async () => {
    try {
      const [tariffsRes, channelsRes, settingsRes] = await Promise.all([
        tariffsApi.getAll(),
        channelsApi.getAll(),
        settingsApi.get().catch(() => ({ data: {} })),
      ]);
      setTariffs(tariffsRes.data);
      setChannels(channelsRes.data);
      setBotUsername(String(settingsRes.data?.bot_username || '').replace(/^@/, '').trim());
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingTariff(null);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const openEditModal = (tariff) => {
    setEditingTariff(tariff);
    setFormData({
      channel_id: tariff.channel_id,
      name: tariff.name,
      price: tariff.price.toString(),
      price_stars: tariff.price_stars?.toString() || '',
      duration_days: tariff.duration_days.toString(),
      description: tariff.description || '',
      renewal_discount_enabled: !!tariff.renewal_discount_enabled,
      renewal_discount_type: tariff.renewal_discount_type || 'percent',
      renewal_discount_value: tariff.renewal_discount_value?.toString() || '',
      renewal_window_before_days:
        tariff.renewal_window_before_days?.toString() || '',
      renewal_window_after_days:
        tariff.renewal_window_after_days?.toString() || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      channel_id: formData.channel_id,
      name: formData.name,
      price: parseFloat(formData.price),
      price_stars: formData.price_stars ? parseInt(formData.price_stars) : null,
      duration_days: parseInt(formData.duration_days),
      description: formData.description,
      renewal_discount_enabled: !!formData.renewal_discount_enabled,
      renewal_discount_type: formData.renewal_discount_type || 'percent',
      renewal_discount_value: formData.renewal_discount_enabled
        ? parseFloat(formData.renewal_discount_value) || 0
        : 0,
      renewal_window_before_days: formData.renewal_discount_enabled
        ? parseInt(formData.renewal_window_before_days) || 0
        : 0,
      renewal_window_after_days: formData.renewal_discount_enabled
        ? parseInt(formData.renewal_window_after_days) || 0
        : 0,
    };

    try {
      if (editingTariff) {
        await tariffsApi.update(editingTariff.id, payload);
        toast.success('Тариф обновлен');
      } else {
        await tariffsApi.create(payload);
        toast.success('Тариф создан');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Ошибка сохранения тарифа');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tariff) => {
    if (!window.confirm(`Удалить тариф "${tariff.name}"?`)) return;

    try {
      await tariffsApi.delete(tariff.id);
      toast.success('Тариф удален');
      fetchData();
    } catch (error) {
      toast.error('Ошибка удаления тарифа');
    }
  };

  const handleToggleActive = async (tariff) => {
    try {
      await tariffsApi.update(tariff.id, { is_active: !tariff.is_active });
      fetchData();
      toast.success(tariff.is_active ? 'Тариф деактивирован' : 'Тариф активирован');
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const filteredTariffs = filterChannel === 'all' 
    ? tariffs 
    : tariffs.filter(t => t.channel_id === filterChannel);

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel?.name || 'Неизвестный канал';
  };

  const copyTariffLink = async (tariffId, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(tariffId);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopiedId(''), 1500);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const formatDuration = (days) => {
    if (days === 1) return '1 день';
    if (days < 5) return `${days} дня`;
    if (days === 7) return '1 неделя';
    if (days === 30) return '1 месяц';
    if (days === 90) return '3 месяца';
    if (days === 365) return '1 год';
    return `${days} дней`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="tariffs-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Тарифы</h1>
          <p className="text-zinc-500">Настройка тарифных планов для подписок</p>
        </div>
        <Button 
          onClick={openCreateModal} 
          className="glow-primary"
          data-testid="add-tariff-btn"
          disabled={channels.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Добавить тариф
        </Button>
      </div>

      {channels.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Tag className="empty-state-icon" />
            <p>Сначала добавьте каналы</p>
          </div>
        </div>
      ) : (
        <>
          {/* Filter */}
          <div className="flex items-center gap-4">
            <Label className="text-zinc-400">Фильтр по каналу:</Label>
            <Select value={filterChannel} onValueChange={setFilterChannel}>
              <SelectTrigger className="w-64 bg-[#121212] border-white/10">
                <SelectValue placeholder="Все каналы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все каналы</SelectItem>
                {channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filteredTariffs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTariffs.map((tariff) => (
                <div 
                  key={tariff.id} 
                  className="card card-hover"
                  data-testid={`tariff-card-${tariff.id}`}
                >
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#2AABEE]" />
                      <span className="text-sm text-zinc-400">
                        {getChannelName(tariff.channel_id)}
                      </span>
                    </div>
                    <Switch
                      checked={tariff.is_active}
                      onCheckedChange={() => handleToggleActive(tariff)}
                    />
                  </div>
                  <div className="card-content">
                    <h3 className="text-xl font-bold text-white mb-2">{tariff.name}</h3>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-3xl font-bold text-[#2AABEE]">{tariff.price}</span>
                      <span className="text-zinc-500">₽</span>
                      {tariff.price_stars && (
                        <span className="ml-2 text-sm text-yellow-400 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {tariff.price_stars}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400 mb-4">
                      <Clock className="w-4 h-4" />
                      {formatDuration(tariff.duration_days)}
                    </div>
                    {tariff.description && (
                      <p className="text-sm text-zinc-500 mb-4">{tariff.description}</p>
                    )}
                    {botUsername && tariff.id && (
                      <TariffDeepLink
                        link={tariffDeepLink(botUsername, tariff.id)}
                        copied={copiedId === tariff.id}
                        onCopy={() => copyTariffLink(tariff.id, tariffDeepLink(botUsername, tariff.id))}
                        testId={`copy-tariff-link-${tariff.id}`}
                      />
                    )}
                    {tariff.renewal_discount_enabled &&
                      Number(tariff.renewal_discount_value) > 0 && (
                        <p className="text-xs text-emerald-400/90 mb-4">
                          Скидка за продление:{' '}
                          {tariff.renewal_discount_type === 'fixed'
                            ? `−${tariff.renewal_discount_value}₽`
                            : `−${tariff.renewal_discount_value}%`}
                          {' · '}
                          {tariff.renewal_window_before_days || 0}д до /{' '}
                          {tariff.renewal_window_after_days || 0}д после
                        </p>
                      )}
                    <div className="flex items-center gap-2 pt-4 border-t border-white/5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(tariff)}
                        className="flex-1"
                        data-testid={`edit-tariff-${tariff.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Редактировать
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(tariff)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-tariff-${tariff.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <div className="empty-state">
                <Tag className="empty-state-icon" />
                <p className="mb-4">Тарифы не добавлены</p>
                <Button onClick={openCreateModal}>
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить первый тариф
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTariff ? 'Редактировать тариф' : 'Добавить тариф'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="channel">Канал</Label>
                <Select
                  value={formData.channel_id}
                  onValueChange={(value) => setFormData({ ...formData, channel_id: value })}
                  required
                >
                  <SelectTrigger className="bg-[#121212] border-white/10" data-testid="tariff-channel-select">
                    <SelectValue placeholder="Выберите канал" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Название тарифа</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Базовый / Премиум / VIP"
                  required
                  data-testid="tariff-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Цена (₽)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="500"
                    required
                    data-testid="tariff-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="price_stars">Цена в Stars (необязательно)</Label>
                  <Input
                    id="price_stars"
                    type="number"
                    min="1"
                    value={formData.price_stars}
                    onChange={(e) => setFormData({ ...formData, price_stars: e.target.value })}
                    placeholder="50"
                    data-testid="tariff-price-stars-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="duration">Длительность (дней)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  placeholder="30"
                  required
                  data-testid="tariff-duration-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Описание</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Что входит в тариф"
                  data-testid="tariff-description-input"
                />
              </div>

              <div className="space-y-3 p-4 bg-zinc-900/30 rounded-lg border border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Скидка за продление</Label>
                    <p className="text-xs text-zinc-500 mt-1">
                      Автоскидка, если пользователь продлевает в окне до/после истечения
                    </p>
                  </div>
                  <Switch
                    checked={formData.renewal_discount_enabled}
                    onCheckedChange={(c) =>
                      setFormData({ ...formData, renewal_discount_enabled: c })
                    }
                    data-testid="tariff-renewal-enabled-switch"
                  />
                </div>

                {formData.renewal_discount_enabled && (
                  <div className="space-y-3 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Тип скидки</Label>
                        <Select
                          value={formData.renewal_discount_type}
                          onValueChange={(v) =>
                            setFormData({ ...formData, renewal_discount_type: v })
                          }
                        >
                          <SelectTrigger className="bg-[#121212] border-white/10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Процент (%)</SelectItem>
                            <SelectItem value="fixed">Сумма (₽)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Значение{' '}
                          {formData.renewal_discount_type === 'percent' ? '(%)' : '(₽)'}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.renewal_discount_value}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              renewal_discount_value: e.target.value,
                            })
                          }
                          placeholder={
                            formData.renewal_discount_type === 'percent' ? '50' : '100'
                          }
                          data-testid="tariff-renewal-value-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Окно «до» (дней до окончания)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.renewal_window_before_days}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              renewal_window_before_days: e.target.value,
                            })
                          }
                          placeholder="3"
                          data-testid="tariff-renewal-before-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Окно «после» (дней после истечения)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.renewal_window_after_days}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              renewal_window_after_days: e.target.value,
                            })
                          }
                          placeholder="2"
                          data-testid="tariff-renewal-after-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Пример: 50% / 3 / 2 — за 3 дня до или в течение 2 дней после
                      истечения пользователь получит скидку 50% на продление.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-tariff-btn">
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
